import { hasLinkedCg } from '@/utils/cg-status';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

// Where "Not now" goes. The All page is where the Next Steps icon lives, so it is
// the honest "up" from anywhere inside the section — unlike a plain back, which
// from the lesson reader would only land on the program landing, where the same
// block is waiting.
const ALL_TAB_PATH = '/tabs/all';

// Kill switch for the entry gate. Read THREE-state (getFlag, not isEnabled) and
// acted on only when it is explicitly false, following applyOtaKillSwitch in
// `src/utils/ota.js`: isEnabled() collapses "off" and "no row exists" into the
// same false, so gating on it would turn a shipped feature off everywhere until
// somebody remembered to create rows — silently, and including production, where
// this gate has been live since prod-v1.2.14-237.5.
//
// So: no row means the gate behaves exactly as it does today. To remove the
// gate, create the flag row with the value FALSE (production, or everywhere) —
// a database change, no deploy and no App Store submission.
//
// It kills the PROACTIVE ask only. The same modal is also raised by
// cgRedeemPending, which appears solely after a member redeemed a book code or
// scanned a leader QR and the SERVER refused to grant without a CG; suppressing
// that would leave them with a spent code, no access, and no way to satisfy the
// requirement — a worse failure than the one this switch exists to undo.
const CG_GATE_FLAG = 'next-steps-cg-gate';

// Decides whether the connect-group hard block stands in front of Next Steps,
// and clears it when the member answers.
//
// A mixin rather than page-local state because the block lives on two surfaces —
// the program landing and the lesson reader — and a member can deeplink straight
// into a lesson without ever passing the landing. Two copies of this rule would
// eventually disagree, and the direction it would fail in is letting someone
// read the course without ever being asked.
//
// Removable without a release: see CG_GATE_FLAG above.
//
// Who is asked: anyone who has no Connect Group, and nobody who has one
// (decided 2026-09-03). The question is only "do we know they have a CG" —
// status is not consulted. A member who reaches a program landing or the lesson
// reader with none on record is asked to link one; a member with one on record
// is left alone. Until then V and N were re-asked every time even with a CG on
// file (2026-07-22), and G and above only when they had none; the owner's rule
// now is: if they already have one, don't ask.
//
// "Has a CG" is the SERVER's answer wherever the access summary carries it
// (`hasCg`: an unended membership on a CG that is not deactivated — the rule the
// leader roster and the unlock QR's MEMBER_HAS_NO_CG read too). The profile's
// cgName is the fallback for an API that predates the field, and it is a weak
// one: it prefers the free-text name typed at registration, so it reads
// "CG Alpha" for a member no CG has on its books and "None" for one who typed
// that — which is why the fallback goes through hasLinkedCg. For a login waiting
// for its leader's link the server's `hasCg` is the CG it claimed at
// registration, and answering the block moves that claim (CgHardBlockModal).
//
// Fails OPEN while the answer is unknown: until the access summary or the
// profile has loaded nothing is shown. A route guard failing closed costs a
// member a redirect; this failing closed would put an undismissable modal over
// a course someone has already paid for.
export default {
  computed: {
    // Set when a redeem came back needsCgLink: the server refused to grant until
    // this member has a main CG. Holding the code lets the same attempt finish
    // by itself once they answer, instead of making them find the card and type
    // it again. Kept in the store because the surface that takes the refusal is
    // often not the one that shows the block.
    // Optional-chained like `pastoralMapping` below, and for the same reason:
    // this mixin is mounted outside the Courses section now (My Vouchers' "Have
    // a code?" button), so the courses module is not guaranteed to be
    // registered on every host's store.
    cgPendingRedeemCode() {
      return this.$store.state.courses?.cgPendingRedeemCode;
    },
    // The leader-unlock lane's equivalent: a scanned leader QR whose redemption
    // stopped at the CG check. Same shape, same reason.
    cgPendingUnlockToken() {
      return this.$store.state.courses?.cgPendingUnlockToken;
    },
    // Same source of truth as the route guard: GET /profile/pastoral-mapping,
    // where V=1, N=2, G=3. Undefined until the mapping resolves. Not part of
    // the block's rule since 2026-09-03; the chooser's intro gate
    // (next-steps.vue) still reads it off this mixin.
    pastoralSort() {
      const roleId = this.$store.getters['profile/pastoralRoleId'];
      return this.$store.state.lookups?.pastoralMapping?.[roleId];
    },
    // The profile store returns undefined for every field until it has loaded,
    // so an empty cgName on its own cannot be read as "no CG" — it also means
    // "not fetched yet". A name is the store's own signal that the fetch landed.
    profileLoaded() {
      const profile = this.$store.state.profile;
      return !!(profile?.givenName || profile?.familyName);
    },
    // The server's answer from GET /courses/access, on a build that gives one.
    // Undefined until the summary has loaded, and on an API that predates it.
    serverHasCg() {
      const hasCg = this.$store.state.courses?.access?.hasCg;
      return typeof hasCg === 'boolean' ? hasCg : undefined;
    },
    // Do we know yet whether this member has a CG at all.
    cgKnown() {
      return this.serverHasCg !== undefined || this.profileLoaded;
    },
    hasMainCg() {
      if (this.serverHasCg !== undefined) return this.serverHasCg;
      return hasLinkedCg(this.$store.getters['profile/cgName']);
    },
    cgHardBlockConfirmed() {
      return !!this.$store.state.courses?.cgHardBlockConfirmed;
    },
    // Whether the block stands in front of the *content*. The chooser does not
    // use this — it grants nothing, so blocking it would only hide the part
    // cards a V/N came to read.
    // Explicitly false = the gate has been turned off from the admin UI. undefined
    // (no row, or the flags API is down) leaves it on — see CG_GATE_FLAG.
    cgGateKilled() {
      // Called from a computed, never at module scope: a module-scope
      // useFeatureFlags() runs at import time and trips specs that mock it.
      const { getFlag } = useFeatureFlags();
      return getFlag(CG_GATE_FLAG) === false;
    },
    needsCgHardBlock() {
      if (this.cgGateKilled) return false;
      if (this.cgHardBlockConfirmed) return false;
      // Asked only once we actually know they have no CG. Anyone with one is
      // left alone, whatever their status.
      return this.cgKnown && !this.hasMainCg;
    },
    // Every surface shows the block while a redemption is waiting on a CG,
    // including the chooser — that is how a scanned card finishes its redeem.
    cgRedeemPending() {
      return !!this.cgPendingRedeemCode || !!this.cgPendingUnlockToken;
    },
  },
  methods: {
    // The server refused to grant until a CG exists. Put the block up and keep
    // the code; onCgLinked finishes the redemption.
    onNeedsCgLink(code) {
      this.$store.commit('courses/setCgPendingRedeemCode', code || null);
    },
    // "Not now" — leave Next Steps with the question still unanswered.
    //
    // Deliberately does NOT mark the member confirmed: they are asked again next
    // time, which is the whole point of letting them out. Any code being held for
    // a retry is dropped rather than carried, because nothing was granted for it
    // and a stale code would redeem itself at some unrelated later moment.
    onCgBlockExit() {
      this.$store.commit('courses/setCgPendingRedeemCode', null);
      this.$store.commit('courses/setCgPendingUnlockToken', null);
      this.$router.push(ALL_TAB_PATH);
    },
    // Only reachable after linkMainCg (claimLoginCg for a login waiting for its
    // link) resolves, so there is no way to clear the block by typing a name the
    // server rejected. The profile refresh keeps cgName honest for the rest of
    // the session.
    async onCgLinked() {
      this.$store.commit('courses/setCgHardBlockConfirmed', true);
      await this.$store.dispatch('profile/fetchProfile').catch(() => {});

      // A leader unlock that stopped at the CG check resumes by re-entering its
      // own page, which redoes the check against the refreshed profile and
      // redeems. The page owns the outcome screens, so nothing is duplicated here.
      const token = this.cgPendingUnlockToken;
      if (token) {
        this.$store.commit('courses/setCgPendingUnlockToken', null);
        this.$router.push(`/courses/unlock/${token}`);
        return;
      }

      const code = this.cgPendingRedeemCode;
      this.$store.commit('courses/setCgPendingRedeemCode', null);
      if (!code) return;

      // Finish the redemption the CG requirement interrupted. Best-effort: if it
      // fails now, the member still has their card and can enter it again — the
      // server has granted nothing, so nothing is lost.
      try {
        const result = await this.$store.dispatch('courses/redeemBookCode', { code });
        const slug = result?.unlockedProgramSlug;
        if (slug) this.$router.push(`/courses/${slug}`);
      } catch (error) {
        console.error('Retrying the book code after linking a CG failed:', error);
      }
    },
  },
};
